import * as RadPopover from '@radix-ui/react-popover'
import styled from 'styled-components'

import { ArrowDown3Icon, Button } from '@blade/ui-kit'

import type { ClassNameProps } from '@root/shared/react'

type Props<T> = {
  options: Array<{ label: string; value: T | undefined }>
  value: T | undefined
  onChange: (value: T | undefined) => void
  open: boolean
  onOpenChange: (open: boolean) => void
} & ClassNameProps

export const TransactionsFilter = <T,>({
  options,
  value,
  open,
  onChange,
  onOpenChange,
}: Props<T>) => {
  const label = options.find(
    (option: { value: T | undefined }) => option.value === value,
  )?.label
  const handleClose = () => onOpenChange(false)

  const handleSelect = (option: T | undefined) => {
    onChange(option)
    onOpenChange(false)
  }

  return (
    <>
      <Root open={open} onOpenChange={onOpenChange}>
        <Trigger open={open} onClick={handleClose} asChild>
          <ButtonAlt
            variant="ghost"
            radius="rounded"
            type="button"
            iconRight={<ArrowDown3Icon size={12} />}
          >
            <ButtonLabel>{label}</ButtonLabel>
          </ButtonAlt>
        </Trigger>
        <RadPopover.Portal>
          <Content side="bottom" align="start">
            <ButtonExpanded
              variant="ghost"
              radius="full"
              iconRight={<ArrowDown3Icon size={12} />}
              onClick={handleClose}
            >
              <ButtonLabel>{label}</ButtonLabel>
            </ButtonExpanded>
            <Items>
              {options
                .filter((option) => option.value !== value)
                .map((option) => (
                  <Item
                    key={option.label}
                    onClick={() => handleSelect(option.value)}
                  >
                    <Label htmlFor={option.label}>{option.label}</Label>
                  </Item>
                ))}
            </Items>
          </Content>
        </RadPopover.Portal>
      </Root>
    </>
  )
}

const Root = styled(RadPopover.Root)``

const Trigger = styled(RadPopover.Trigger)<{
  open: boolean
}>``

const Content = styled(RadPopover.Content)`
  width: var(--radix-popper-anchor-width);
  border: 0.8px solid var(--background-5);

  backdrop-filter: blur(35px);
  border-radius: 6px;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  margin-top: -20.5px;
`

const Items = styled.div`
  padding: 4px 6px 4px;
  gap: 4px;
  display: grid;
`

const Item = styled.div`
  color: var(--textA-8);
  width: 100%;

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  transition: all 100ms ease-in;

  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  white-space: nowrap;

  &:hover {
    color: var(--text-12);
    cursor: pointer;
  }
`

const Label = styled.label`
  cursor: pointer;
`

const ButtonAlt = styled(Button)`
  border: 0.8px solid transparent;
  --button-radius-rounded: 6px;

  height: 20px;
  font-size: 11px;
  font-weight: 600;
  padding-inline: 6px;

  display: grid;

  grid-template-columns: auto auto;
  justify-content: start;
  justify-items: start;

  text-overflow: ellipsis;
  white-space: nowrap;

  transition: all 100ms ease-in;

  > span {
    color: var(--textA-8);
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  svg {
    color: var(--textA-8);
  }

  &:hover {
    background: none;
    span,
    svg {
      color: var(--textA-11);
    }
  }
`

const ButtonExpanded = styled(ButtonAlt)`
  --button-radius-full: 0;
  border: 1px solid transparent;
  background: transparent;

  grid-template-columns: 1fr auto;

  gap: 0;
  width: 100%;
  cursor: pointer;
  padding-inline: 5px;
  border-bottom: 1px solid var(--background-4);

  &:hover {
    background: transparent;
  }
  span,
  svg {
    color: var(--text-12);
  }
`

const ButtonLabel = styled.span``
